/* eslint-disable react/function-component-definition */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { TABLE_ICONS } from '../utils/tableIcons';
import { useSnackbar } from '../hooks/useSnackbar';
import Snackbar from '../components/Snackbar';
import './LeaderUploads.css';

function LeaderUploads() {
  const { id } = useParams(); // leader id
  const navigate = useNavigate();
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadsBySub, setUploadsBySub] = useState({});
  const [leaderName, setLeaderName] = useState('');
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Form states
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [editSubcategoryName, setEditSubcategoryName] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [deleteSubcategoryId, setDeleteSubcategoryId] = useState(null);
  const [uploadSubcategoryId, setUploadSubcategoryId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const fileInputRef = useRef(null);

  // Helper function to create thumbnails for images or videos
  const createThumbnailForFile = async (file, maxSizeKB = 80) => {
    if (file.type.startsWith('image/')) {
      return await createThumbnail(file, maxSizeKB);
    } else if (file.type.startsWith('video/')) {
      return await createVideoThumbnail(file, maxSizeKB);
    } else {
      throw new Error('Unsupported file type for thumbnail creation');
    }
  };

  // Image compression utility to create thumbnails under 80KB
  const createThumbnail = async (file, maxSizeKB = 80) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        const maxDimension = 800; // Max width or height for thumbnail

        if (width > height) {
          if (width > maxDimension) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress the image
        ctx.drawImage(img, 0, 0, width, height);

        // Start with quality 0.8 and reduce until under maxSizeKB
        let quality = 0.8;
        let compressedDataUrl;

        const compress = () => {
          compressedDataUrl = canvas.toDataURL('image/jpeg', quality);

          // Convert data URL to blob to check size
          const byteString = atob(compressedDataUrl.split(',')[1]);
          const sizeKB = byteString.length / 1024;

          if (sizeKB > maxSizeKB && quality > 0.1) {
            quality -= 0.1;
            compress();
          } else {
            // Convert data URL to File
            const byteNumbers = new Array(byteString.length);
            for (let i = 0; i < byteString.length; i++) {
              byteNumbers[i] = byteString.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const thumbnailFile = new File([byteArray], `thumb_${file.name}`, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(thumbnailFile);
          }
        };

        compress();
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // Video thumbnail generation utility
  const createVideoThumbnail = async (file, maxSizeKB = 80) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      video.preload = 'metadata';
      video.muted = true;

      video.onloadedmetadata = () => {
        // Seek to 1 second or 10% of video duration, whichever is smaller
        const seekTime = Math.min(1, video.duration * 0.1);
        video.currentTime = seekTime;
      };

      video.onseeked = () => {
        try {
          // Calculate thumbnail dimensions while maintaining aspect ratio
          let { videoWidth, videoHeight } = video;
          const maxDimension = 800; // Max width or height for thumbnail

          if (videoWidth > videoHeight) {
            if (videoWidth > maxDimension) {
              videoHeight = (videoHeight * maxDimension) / videoWidth;
              videoWidth = maxDimension;
            }
          } else {
            if (videoHeight > maxDimension) {
              videoWidth = (videoWidth * maxDimension) / videoHeight;
              videoHeight = maxDimension;
            }
          }

          canvas.width = videoWidth;
          canvas.height = videoHeight;

          // Draw video frame to canvas
          ctx.drawImage(video, 0, 0, videoWidth, videoHeight);

          // Compress the thumbnail
          let quality = 0.8;
          let compressedDataUrl;

          const compress = () => {
            compressedDataUrl = canvas.toDataURL('image/jpeg', quality);

            // Convert data URL to blob to check size
            const byteString = atob(compressedDataUrl.split(',')[1]);
            const sizeKB = byteString.length / 1024;

            if (sizeKB > maxSizeKB && quality > 0.1) {
              quality -= 0.1;
              compress();
            } else {
              // Convert data URL to File
              const byteNumbers = new Array(byteString.length);
              for (let i = 0; i < byteString.length; i++) {
                byteNumbers[i] = byteString.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const thumbnailFile = new File([byteArray], `thumb_${file.name.split('.')[0]}.jpg`, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });

              // Clean up
              URL.revokeObjectURL(video.src);
              resolve(thumbnailFile);
            }
          };

          compress();
        } catch (error) {
          console.error('Error creating video thumbnail:', error);
          URL.revokeObjectURL(video.src);
          reject(error);
        }
      };

      video.onerror = (error) => {
        console.error('Video loading error:', error);
        URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video for thumbnail generation'));
      };

      video.src = URL.createObjectURL(file);
    });
  };

  // File validation helper
  const validateFiles = (files) => {
    const maxFileSize = 50 * 1024 * 1024; // 50MB for videos, 10MB for images
    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const allowedVideoTypes = ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/webm', 'video/mkv'];
    const errors = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isImage = allowedImageTypes.includes(file.type);
      const isVideo = allowedVideoTypes.includes(file.type);

      // Check file type first
      if (!isImage && !isVideo) {
        errors.push(`${file.name}: Invalid file type. Only images (JPEG, PNG, GIF, WebP) and videos (MP4, AVI, MOV, WMV, FLV, WebM, MKV) are allowed`);
        continue;
      }

      // Check file size based on type
      const maxSizeForFile = isImage ? 10 * 1024 * 1024 : maxFileSize; // 10MB for images, 50MB for videos
      if (file.size > maxSizeForFile) {
        const maxSizeText = isImage ? '10MB' : '50MB';
        errors.push(`${file.name}: File size too large (max ${maxSizeText} for ${isImage ? 'images' : 'videos'})`);
      }
    }

    return errors;
  };

  const fetchLeaderData = useCallback(async () => {
    try {
      const leaderDoc = await getDoc(doc(db, 'myleaders', id));
      if (leaderDoc.exists()) {
        setLeaderName(leaderDoc.data().name || 'Unknown Leader');
      }
    } catch (error) {
      console.error('Error fetching leader data:', error);
    }
  }, [id]);

  const fetchSubcategories = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch subcategories from myleaders/{leaderId}/subcategories
      const subcategoriesSnapshot = await getDocs(collection(db, 'myleaders', id, 'subcategories'));

      const subcategoriesData = [];

      for (const docSnap of subcategoriesSnapshot.docs) {
        const data = docSnap.data();

        // Count uploads in the upload subcollection
        let uploadCount = 0;
        try {
          const uploadsSnapshot = await getDocs(collection(db, 'myleaders', id, 'subcategories', docSnap.id, 'upload'));
          uploadCount = uploadsSnapshot.size;
        } catch (error) {
          console.warn('Error counting uploads for subcategory:', docSnap.id, error);
        }

        subcategoriesData.push({
          id: docSnap.id,
          name: data.name || '',
          startDate: data.startDate || '',
          endDate: data.endDate || '',
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          uploadCount: uploadCount
        });
      }

      // Sort manually by createdAt if available, otherwise by name
      subcategoriesData.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.toDate() - a.createdAt.toDate();
        }
        return a.name.localeCompare(b.name);
      });

      setSubCategories(subcategoriesData);

      // Fetch uploads for each subcategory
      await fetchAllUploads(subcategoriesData);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch leader data and subcategories
  useEffect(() => {
    if (id) {

      fetchLeaderData();
      fetchSubcategories();
    }
  }, [id, fetchLeaderData, fetchSubcategories]);

  const fetchAllUploads = async (subcategories) => {
    try {
      const uploadsData = {};

      for (const subcategory of subcategories) {

        // Fetch from upload subcollection
        const uploadsSnapshot = await getDocs(collection(db, 'myleaders', id, 'subcategories', subcategory.id, 'upload'));

        const uploads = [];

        uploadsSnapshot.forEach((docSnap) => {
          const data = docSnap.data();

          uploads.push({
            id: docSnap.id,
            filename: data.fileName || data.filename || '',
            originalName: data.originalName || data.name || '',
            url: data.downloadURL || data.url || '',
            downloadURL: data.downloadURL || data.url || '',
            thumbnailImg: data.thumbnailImg || null, // Add thumbnail URL
            fileSize: data.fileSize || 0,
            fileType: data.fileType || '',
            createdAt: data.createdAt,
            updatedAt: data.updatedAt
          });
        });

        // Sort by createdAt (newest first)
        uploads.sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return b.createdAt.toDate() - a.createdAt.toDate();
          }
          return 0;
        });

        uploadsData[subcategory.id] = uploads;
      }

      // Log thumbnail availability for performance monitoring
      const allUploads = Object.values(uploadsData).flat();
      const withThumbnails = allUploads.filter(upload => upload.thumbnailImg).length;
      const totalUploads = allUploads.length;
      if (totalUploads > 0) {

      }

      setUploadsBySub(uploadsData);
    } catch (error) {
      console.error('Error fetching uploads:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const isSubcategoryActive = (subcategory) => {
    const currentDate = new Date();
    const startDate = subcategory.startDate ? new Date(subcategory.startDate) : null;
    const endDate = subcategory.endDate ? new Date(subcategory.endDate) : null;

    if (!startDate && !endDate) return true;
    if (startDate && !endDate) return currentDate >= startDate;
    if (!startDate && endDate) return currentDate <= endDate;
    if (startDate && endDate) return currentDate >= startDate && currentDate <= endDate;

    return true;
  };

  // Add subcategory
  const handleAddSubcategory = async () => {
    if (!newSubcategoryName.trim()) {
      alert('Please enter a subcategory name');
      return;
    }

    try {
      setLoading(true);

      // Add to myleaders/{leaderId}/subcategories
      const docRef = await addDoc(collection(db, 'myleaders', id, 'subcategories'), {
        name: newSubcategoryName.trim(),
        startDate: newStartDate || null,
        endDate: newEndDate || null,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // Reset form
      setNewSubcategoryName('');
      setNewStartDate('');
      setNewEndDate('');
      setShowAddModal(false);

      // Refresh data
      await fetchSubcategories();

    } catch (error) {
      console.error('Error adding subcategory:', error);
      alert('Failed to add subcategory. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Edit subcategory
  const handleEditSubcategory = (subcategory) => {
    setEditingSubcategory(subcategory);
    setEditSubcategoryName(subcategory.name);
    setEditStartDate(subcategory.startDate || '');
    setEditEndDate(subcategory.endDate || '');
    setShowEditModal(true);
  };

  const handleUpdateSubcategory = async () => {
    if (!editSubcategoryName.trim()) {
      alert('Please enter a subcategory name');
      return;
    }

    try {
      setLoading(true);
      // Update in myleaders/{leaderId}/subcategories
      await updateDoc(doc(db, 'myleaders', id, 'subcategories', editingSubcategory.id), {
        name: editSubcategoryName.trim(),
        startDate: editStartDate || null,
        endDate: editEndDate || null,
        updatedAt: new Date()
      });

      setShowEditModal(false);
      setEditingSubcategory(null);
      setEditSubcategoryName('');
      setEditStartDate('');
      setEditEndDate('');

      // Refresh data
      await fetchSubcategories();

    } catch (error) {
      console.error('Error updating subcategory:', error);
      alert('Failed to update subcategory. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Delete subcategory
  const handleDeleteSubcategory = (subcategoryId) => {
    setDeleteSubcategoryId(subcategoryId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);

      // Delete all uploads from the upload subcollection
      const uploadsSnapshot = await getDocs(collection(db, 'myleaders', id, 'subcategories', deleteSubcategoryId, 'upload'));

      // Delete files from storage and documents from firestore
      for (const uploadDoc of uploadsSnapshot.docs) {
        const uploadData = uploadDoc.data();

        // Delete original file from storage
        if (uploadData.downloadURL || uploadData.url) {
          try {
            const storageRef = ref(storage, uploadData.downloadURL || uploadData.url);
            await deleteObject(storageRef);
          } catch (storageError) {
            console.error('Error deleting file from storage:', storageError);
          }
        }

        // Delete thumbnail from storage if it exists
        if (uploadData.thumbnailImg) {
          try {
            const thumbnailRef = ref(storage, uploadData.thumbnailImg);
            await deleteObject(thumbnailRef);
          } catch (storageError) {
            console.error('Error deleting thumbnail from storage:', storageError);
          }
        }
        await deleteDoc(uploadDoc.ref);
      }

      // Delete the subcategory
      await deleteDoc(doc(db, 'myleaders', id, 'subcategories', deleteSubcategoryId));

      setShowDeleteConfirm(false);
      setDeleteSubcategoryId(null);

      // Refresh data
      await fetchSubcategories();

      showSnackbar('Subcategory and all associated uploads deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      showSnackbar('Failed to delete subcategory. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // File upload functions
  const handleUploadClick = (subcategoryId) => {
    setUploadSubcategoryId(subcategoryId);
    setSelectedFiles([]);
    setShowUploadModal(true);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      setSelectedFiles(files);
    }
  };

  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleUploadFiles = async () => {
    if (!selectedFiles.length) {
      alert('Please select files to upload');
      return;
    }

    // Validate files before upload
    const validationErrors = validateFiles(selectedFiles);
    if (validationErrors.length > 0) {
      alert(`Upload failed: ${validationErrors.join(', ')}`);
      return;
    }

    try {
      setLoading(true);
      const uploadPromises = selectedFiles.map(async (file, index) => {
        // Create unique filename with leader and subcategory info
        const timestamp = Date.now() + index; // Add index to ensure unique timestamps
        const filename = `${timestamp}-${id}-${uploadSubcategoryId}-${file.name}`;
        const storagePath = `leaders/${filename}`;

        // Create and upload thumbnail for both images and videos

        let thumbnailURL = null;
        let downloadURL;

        try {
          const thumbnailFile = await createThumbnailForFile(file);

          const thumbnailFileName = `thumb_${timestamp}-${id}-${uploadSubcategoryId}-${file.name.split('.')[0]}.jpg`;
          const thumbnailStoragePath = `leaders/thumbnails/${thumbnailFileName}`;
          const thumbnailStorageRef = ref(storage, thumbnailStoragePath);

          // Upload both original and thumbnail files
          const [snapshot, thumbnailSnapshot] = await Promise.all([
            uploadBytes(ref(storage, storagePath), file),
            uploadBytes(thumbnailStorageRef, thumbnailFile)
          ]);

          const [fileURL, thumbURL] = await Promise.all([
            getDownloadURL(snapshot.ref),
            getDownloadURL(thumbnailSnapshot.ref)
          ]);

          downloadURL = fileURL;
          thumbnailURL = thumbURL;

        } catch (error) {
          console.error('Error creating thumbnail:', error);
          // If thumbnail creation fails, still upload the original file
          const storageRef = ref(storage, storagePath);
          const snapshot = await uploadBytes(storageRef, file);
          downloadURL = await getDownloadURL(snapshot.ref);

        }

        // Save metadata to upload subcollection with thumbnail
        const uploadData = {
          fileName: filename,
          originalName: file.name,
          name: file.name.split('.')[0],
          downloadURL: downloadURL,
          url: downloadURL,
          thumbnailImg: thumbnailURL, // Add thumbnail URL
          fileSize: file.size,
          fileType: file.type,
          storagePath: storagePath,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        await addDoc(collection(db, 'myleaders', id, 'subcategories', uploadSubcategoryId, 'upload'), uploadData);
      });

      await Promise.all(uploadPromises);

      setShowUploadModal(false);
      setSelectedFiles([]);
      setUploadSubcategoryId(null);

      // Refresh uploads
      await fetchSubcategories();

    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload files. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Delete single upload
  const handleDeleteUpload = async (uploadId, subcategoryId, downloadURL) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      setLoading(true);

      // Get upload data to check for thumbnail
      const uploadDoc = await getDoc(doc(db, 'myleaders', id, 'subcategories', subcategoryId, 'upload', uploadId));
      const uploadData = uploadDoc.data();

      // Delete original file from storage
      if (downloadURL) {
        try {
          await deleteObject(ref(storage, downloadURL));
        } catch (storageError) {
          console.error('Error deleting file from storage:', storageError);
        }
      }

      // Delete thumbnail from storage if it exists
      if (uploadData && uploadData.thumbnailImg) {
        try {
          await deleteObject(ref(storage, uploadData.thumbnailImg));
        } catch (storageError) {
          console.error('Error deleting thumbnail from storage:', storageError);
        }
      }

      // Delete from Firestore upload subcollection
      await deleteDoc(doc(db, 'myleaders', id, 'subcategories', subcategoryId, 'upload', uploadId));

      // Refresh uploads
      await fetchSubcategories();

      showSnackbar('File and thumbnail deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting upload:', error);
      showSnackbar('Failed to delete upload. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="leader-uploads-container">
        <div className="loading">Loading leader uploads...</div>
      </div>
    );
  }

  return (
    <div className="leader-uploads-container">
      <div className="leader-uploads-header">
        <h1>Posters For - {leaderName}</h1>
        <button
          className="back-btn"
          onClick={() => navigate('/admin/leaders')}
        >
          ← Back to Leaders
        </button>
      </div>

      <div className="subcategories-section">
        <div className="subcategories-header">
          <h2>Subcategories</h2>
          <button
            className="btn btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            Add Subcategory
          </button>
        </div>

        <table className="subCategories-table">
          <thead>
            <tr>
              <th>Sr. No</th>
              <th>Subcategory Name</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {subCategories.map((sub, index) => (
              <tr key={sub.id}>
                <td>{index + 1}</td>
                <td>{sub.name}</td>
                <td>{formatDate(sub.startDate)}</td>
                <td>{formatDate(sub.endDate)}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-icon"
                      onClick={() => handleEditSubcategory(sub)}
                      title="Edit"
                    >
                      {TABLE_ICONS.EDIT}
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleUploadClick(sub.id)}
                      title="Upload Files"
                    >
                      {TABLE_ICONS.FOLDER}
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleDeleteSubcategory(sub.id)}
                      title="Delete"
                    >
                      {TABLE_ICONS.DELETE}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* SubCategory Posters Display */}
        {subCategories.map((sub) => {
          const uploadsForSub = uploadsBySub[sub.id] || [];
          const hasUploads = uploadsForSub.length > 0;
          const isActive = isSubcategoryActive(sub);

          return (
            <div key={sub.id} className="subcategory-posters">
              <div className="subcategory-header">
                <div className="subcategory-info">
                  <h3>
                    {sub.name}
                    <span className="image-count">
                      ({uploadsForSub.length} {uploadsForSub.length === 1 ? 'file' : 'files'})
                    </span>
                  </h3>
                  {(sub.startDate || sub.endDate) && (
                    <p className="date-range">
                      {sub.startDate && sub.endDate
                        ? `${formatDate(sub.startDate)} - ${formatDate(sub.endDate)}`
                        : sub.startDate
                          ? `${formatDate(sub.startDate)} onwards`
                          : `Until ${formatDate(sub.endDate)}`
                      }
                    </p>
                  )}
                </div>
              </div>

              {hasUploads ? (
                <div className="images-grid">
                  {uploadsForSub.map((item) => {
                    const isVideo = item.fileType && item.fileType.startsWith('video/');
                    return (
                      <div key={item.id} className="image-card">
                        {isVideo ? (
                          <div className="video-preview">
                            <video
                              src={item.url}
                              poster={item.thumbnailImg}
                              className="poster-video"
                              controls
                              preload="metadata"
                              controlsList="nodownload"
                              disablePictureInPicture={false}
                              style={{ position: 'relative', zIndex: 0 }}
                            />
                            <div className="video-overlay">
                              <span className="video-icon">🎥</span>
                            </div>
                          </div>
                        ) : (
                          <img
                            src={item.thumbnailImg || item.url}
                            alt={item.filename}
                            className="poster-image"
                            onError={(e) => {
                              // If thumbnail fails to load, fallback to original image
                              if (e.target.src !== item.url && item.url) {
                                e.target.src = item.url;
                              }
                            }}
                            loading="lazy"
                          />
                        )}
                        <button
                          className="delete-image-btn"
                          title={`Delete ${isVideo ? 'video' : 'image'}`}
                          onClick={() => handleDeleteUpload(item.id, sub.id, item.downloadURL || item.url)}
                        >
                          {TABLE_ICONS.DELETE}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="no-uploads-message">No uploads within this date range</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Subcategory Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add Subcategory</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setShowAddModal(false);
                  setNewSubcategoryName('');
                  setNewStartDate('');
                  setNewEndDate('');
                }}
              >
                ×
              </button>
            </div>

            <div className="form-group">
              <input
                type="text"
                placeholder="Subcategory Name"
                value={newSubcategoryName}
                onChange={(e) => setNewSubcategoryName(e.target.value)}
              />
            </div>

            <div className="form-group">
              <input
                type="date"
                placeholder="Start Date"
                value={newStartDate}
                onChange={(e) => setNewStartDate(e.target.value)}
              />
            </div>

            <div className="form-group">
              <input
                type="date"
                placeholder="End Date"
                value={newEndDate}
                onChange={(e) => setNewEndDate(e.target.value)}
              />
            </div>

            <div className="form-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddModal(false);
                  setNewSubcategoryName('');
                  setNewStartDate('');
                  setNewEndDate('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddSubcategory}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Subcategory Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Subcategory</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingSubcategory(null);
                  setEditSubcategoryName('');
                  setEditStartDate('');
                  setEditEndDate('');
                }}
              >
                ×
              </button>
            </div>

            <div className="form-group">
              <input
                type="text"
                placeholder="Subcategory Name"
                value={editSubcategoryName}
                onChange={(e) => setEditSubcategoryName(e.target.value)}
              />
            </div>

            <div className="form-group">
              <input
                type="date"
                placeholder="Start Date"
                value={editStartDate}
                onChange={(e) => setEditStartDate(e.target.value)}
              />
            </div>

            <div className="form-group">
              <input
                type="date"
                placeholder="End Date"
                value={editEndDate}
                onChange={(e) => setEditEndDate(e.target.value)}
              />
            </div>

            <div className="form-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingSubcategory(null);
                  setEditSubcategoryName('');
                  setEditStartDate('');
                  setEditEndDate('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateSubcategory}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="confirm-delete-modal">
            <div className="confirm-delete-header">
              <h3>Confirm Delete</h3>
              <button
                className="close-btn-confirm"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteSubcategoryId(null);
                }}
              >
                ×
              </button>
            </div>

            <div className="confirm-delete-body">
              <div className="warning-triangle">
                ⚠️
              </div>
              <p className="delete-message">Are you sure you want to delete this subcategory? This will also delete all associated uploads.</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>

            <div className="confirm-delete-actions">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteSubcategoryId(null);
                }}
              >
                CANCEL
              </button>
              <button
                className="delete-btn"
                onClick={handleConfirmDelete}
              >
                DELETE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Files Modal */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal-content upload-modal">
            <div className="modal-header">
              <h3>Upload Files</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFiles([]);
                  setUploadSubcategoryId(null);
                }}
              >
                ×
              </button>
            </div>

            <div
              className="drag-drop-zone"
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={selectedFiles.length === 0 ? handleDropZoneClick : undefined}
            >
              {selectedFiles.length === 0 ? (
                <div className="drag-drop-content">
                  <div className="upload-icon">{TABLE_ICONS.FOLDER}</div>
                  <p className="drag-drop-text">
                    Drag & drop your images and videos here
                  </p>
                  <p className="drag-drop-subtext">
                    or click to browse files
                  </p>
                  <div className="supported-formats">
                    Images: JPG, PNG, GIF, WebP (max 10MB)<br />
                    Videos: MP4, AVI, MOV, WMV, FLV, WebM, MKV (max 50MB)
                  </div>
                </div>
              ) : (
                <div className="files-in-dropzone">
                  <p className="files-selected-text">{selectedFiles.length} files selected</p>
                  <div className="files-preview-grid">
                    {selectedFiles.map((file, index) => {
                      const isImage = file.type.startsWith('image/');
                      const isVideo = file.type.startsWith('video/');
                      return (
                        <div key={index} className="file-preview-item">
                          <div className="preview-container">
                            {isImage ? (
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="preview-thumbnail"
                              />
                            ) : isVideo ? (
                              <video
                                src={URL.createObjectURL(file)}
                                className="preview-thumbnail"
                                controls={false}
                                muted
                              />
                            ) : (
                              <div className="file-icon">{TABLE_ICONS.FILE}</div>
                            )}
                            <button
                              type="button"
                              className="remove-file-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                const newFiles = selectedFiles.filter((_, i) => i !== index);
                                setSelectedFiles(newFiles);
                              }}
                            >
                              ×
                            </button>
                          </div>
                          <p className="file-name">{file.name}</p>
                        </div>
                      );
                    })}

                    {/* Add More Button */}
                    <div className="file-preview-item add-more-item">
                      <div
                        className="add-more-card"
                        onClick={(e) => {
                          e.stopPropagation();
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.multiple = true;
                          input.accept = 'image/*,video/*';
                          input.onchange = (e) => {
                            const newFiles = Array.from(e.target.files || []);
                            if (newFiles.length > 0) {
                              setSelectedFiles([...selectedFiles, ...newFiles]);
                            }
                          };
                          input.click();
                        }}
                      >
                        <div className="add-more-icon">+</div>
                        <p className="add-more-text">Add More</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleFileSelect}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />
            </div>

            <div className="form-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFiles([]);
                  setUploadSubcategoryId(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFiles([]);
                }}
                disabled={selectedFiles.length === 0}
              >
                Clear All
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUploadFiles}
                disabled={selectedFiles.length === 0}
              >
                Upload {selectedFiles.length > 0 && `(${selectedFiles.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      {snackbar.show && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={hideSnackbar}
        />
      )}
    </div>
  );
}

export default LeaderUploads;
